import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { MemberRole, getRoleColor, getRoleDisplayName } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { RoleBadge } from "@/components/members/RoleBadge";
import { Badge } from "@/components/ui/badge";
import { X, Search, UserPlus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getSuggestedRolesForCategory } from "@/lib/permissions";

interface UnaMember {
  id: string;
  full_name: string;
  email: string | null;
  member_role: MemberRole;
  user_id: string | null;
  last_login: string | null;
}

interface MemberAssignmentSelectorProps {
  unaId: string;
  selectedMemberIds: string[];
  onSelectionChange: (memberIds: string[]) => void;
  taskCategory?: string;
  required?: boolean;
  error?: string;
}

export function MemberAssignmentSelector({
  unaId,
  selectedMemberIds,
  onSelectionChange,
  taskCategory,
  required = false,
  error,
}: MemberAssignmentSelectorProps) {
  const [members, setMembers] = useState<UnaMember[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<UnaMember[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    fetchMembers();
  }, [unaId]);

  useEffect(() => {
    filterMembers();
  }, [searchQuery, roleFilter, members]);

  const fetchMembers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("una_members")
      .select("*")
      .eq("una_id", unaId)
      .eq("status", "active")
      .order("full_name");

    if (error) {
      console.error("Error fetching members:", error);
    } else {
      setMembers(data || []);
    }
    setLoading(false);
  };

  const filterMembers = () => {
    let filtered = members;

    // Filter by role
    if (roleFilter !== "all") {
      filtered = filtered.filter((m) => m.member_role === roleFilter);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.full_name.toLowerCase().includes(query) ||
          m.email?.toLowerCase().includes(query) ||
          getRoleDisplayName(m.member_role).toLowerCase().includes(query)
      );
    }

    setFilteredMembers(filtered);
  };

  const selectedMembers = members.filter((m) => selectedMemberIds.includes(m.id));
  const suggestedRoles = taskCategory ? getSuggestedRolesForCategory(taskCategory) : [];
  const suggestedMembers = members.filter((m) => suggestedRoles.includes(m.member_role));

  const addMember = (memberId: string) => {
    if (!selectedMemberIds.includes(memberId)) {
      onSelectionChange([...selectedMemberIds, memberId]);
    }
    setShowDropdown(false);
    setSearchQuery("");
  };

  const removeMember = (memberId: string) => {
    onSelectionChange(selectedMemberIds.filter((id) => id !== memberId));
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const isOnline = (lastLogin: string | null) => {
    if (!lastLogin) return false;
    const daysSinceLogin = (Date.now() - new Date(lastLogin).getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceLogin < 7;
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>
          Assign To Members {required && <span className="text-destructive">*</span>}
        </Label>
        <p className="text-sm text-muted-foreground">
          Select UNA members who will work on this task
        </p>
      </div>

      {/* Smart Suggestions */}
      {suggestedMembers.length > 0 && taskCategory && (
        <div className="p-4 border rounded-lg bg-muted/50">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">💡</span>
            <h4 className="font-semibold">Suggested Members for {taskCategory} Tasks</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {suggestedMembers.slice(0, 3).map((member) => (
              <Button
                key={member.id}
                variant="outline"
                size="sm"
                onClick={() => addMember(member.id)}
                disabled={selectedMemberIds.includes(member.id)}
                className="gap-2"
              >
                <Avatar className="h-5 w-5">
                  <AvatarFallback
                    className="text-xs"
                    style={{ backgroundColor: getRoleColor(member.member_role) + "20" }}
                  >
                    {getInitials(member.full_name)}
                  </AvatarFallback>
                </Avatar>
                {member.full_name}
                <RoleBadge role={member.member_role} size="small" abbreviated />
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Selected Members Display */}
      {selectedMembers.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm">Selected Members ({selectedMembers.length})</Label>
          <div className="flex flex-wrap gap-2">
            {selectedMembers.slice(0, 5).map((member) => (
              <Badge key={member.id} variant="secondary" className="gap-2 pr-1 py-1">
                <Avatar className="h-5 w-5">
                  <AvatarFallback className="text-xs">
                    {getInitials(member.full_name)}
                  </AvatarFallback>
                </Avatar>
                <span>{member.full_name}</span>
                <RoleBadge role={member.member_role} size="small" abbreviated />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 hover:bg-destructive hover:text-destructive-foreground"
                  onClick={() => removeMember(member.id)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            ))}
            {selectedMembers.length > 5 && (
              <Badge variant="outline">+{selectedMembers.length - 5} more</Badge>
            )}
          </div>
        </div>
      )}

      {/* Member Selector */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search members by name, email, or role..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              className="pl-9"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="managing_member">Managing Member</SelectItem>
              <SelectItem value="financial_specialist">Financial Specialist</SelectItem>
              <SelectItem value="legal_specialist">Legal Specialist</SelectItem>
              <SelectItem value="tax_specialist">Tax Specialist</SelectItem>
              <SelectItem value="operations_manager">Operations Manager</SelectItem>
              <SelectItem value="board_member_active">Active Board Member</SelectItem>
              <SelectItem value="board_member_passive">Passive Board Member</SelectItem>
              <SelectItem value="investment_advisor">Investment Advisor</SelectItem>
              <SelectItem value="family_liaison">Family Liaison</SelectItem>
              <SelectItem value="observer">Observer</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {showDropdown && searchQuery && (
          <div className="border rounded-lg bg-background shadow-lg">
            <ScrollArea className="h-[300px]">
              <div className="p-2">
                {loading ? (
                  <div className="text-center py-4 text-muted-foreground">Loading members...</div>
                ) : filteredMembers.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground">No members found</div>
                ) : (
                  <div className="space-y-1">
                    {filteredMembers.map((member) => (
                      <button
                        key={member.id}
                        onClick={() => addMember(member.id)}
                        disabled={selectedMemberIds.includes(member.id)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="relative">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback
                              style={{
                                backgroundColor: getRoleColor(member.member_role) + "20",
                                color: getRoleColor(member.member_role),
                              }}
                            >
                              {getInitials(member.full_name)}
                            </AvatarFallback>
                          </Avatar>
                          {isOnline(member.last_login) && (
                            <div className="absolute bottom-0 right-0 h-3 w-3 bg-green-500 rounded-full border-2 border-background" />
                          )}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold">{member.full_name}</div>
                          {member.email && (
                            <div className="text-sm text-muted-foreground">{member.email}</div>
                          )}
                        </div>
                        <RoleBadge role={member.member_role} size="small" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>

      {/* Warnings */}
      {selectedMembers.some((m) => m.member_role === "board_member_passive" || m.member_role === "observer") && (
        <Alert>
          <AlertDescription>
            ⚠️ You've assigned this task to Passive Board Members or Observers who have limited task permissions.
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {required && selectedMemberIds.length === 0 && (
        <p className="text-sm text-muted-foreground">
          <UserPlus className="h-4 w-4 inline mr-1" />
          At least one member must be assigned to this task
        </p>
      )}
    </div>
  );
}
